import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DashboardTeam } from '@lms/shared-types'
import { Card } from '../primitives/Card'
import { Severity } from '../primitives/Severity'
import { getDashboard } from '../../api/dashboard'
import type { TightDayInput } from '../../lib/manager-queue'

export interface CoverageRailProps {
  team: DashboardTeam | undefined
  /** This week's WeekTriageDay[] — comes from the manager dashboard payload. */
  weekDays?: ReadonlyArray<TightDayInput> | undefined
  /** Monday of this week (ISO). Used to derive next week's Monday for the
   *  expand fetch. Optional — when omitted, week-2 stays hidden. */
  weekStart?: string | undefined
  horizonDays: number
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function shortWeekday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return ''
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return WEEKDAY_LABELS[dow] ?? ''
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

interface DayCellProps {
  day: TightDayInput
  isToday: boolean
}

function DayCell({ day, isToday }: DayCellProps): JSX.Element {
  const slack = day.slotsTotal - day.onLeaveApproved - day.onLeavePending
  const inactive = day.isWeekend || day.isHoliday
  const kind: 'high' | 'medium' | 'info' = inactive
    ? 'info'
    : slack <= 0
      ? 'high'
      : slack === 1
        ? 'medium'
        : 'info'

  const bg = inactive ? 'transparent' : kind === 'high' ? 'var(--danger-soft, rgba(220,38,38,0.08))' : kind === 'medium' ? 'var(--warning-soft, rgba(217,119,6,0.08))' : 'transparent'

  return (
    <div
      data-testid="coverage-rail-day"
      data-iso={day.date}
      style={{
        flex: 1,
        minWidth: 36,
        padding: '4px 6px',
        textAlign: 'center',
        border: isToday ? '1px solid var(--text-3)' : '1px solid var(--french-gray-2)',
        borderRadius: 6,
        background: bg,
        opacity: inactive ? 0.55 : 1,
        fontSize: 11,
        color: 'var(--text-2)',
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{shortWeekday(day.date)}</div>
      <div>
        {inactive ? '—' : `${day.onLeaveApproved + day.onLeavePending}/${day.slotsTotal}`}
      </div>
    </div>
  )
}

interface WeekRowProps {
  label: string
  days: ReadonlyArray<TightDayInput>
  todayIso: string
}

function WeekRow({ label, days, todayIso }: WeekRowProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 56, fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>
        {label}
      </span>
      <div style={{ display: 'flex', flex: 1, gap: 4 }}>
        {days.map((d) => (
          <DayCell key={d.date} day={d} isToday={d.date === todayIso} />
        ))}
      </div>
    </div>
  )
}

/**
 * Single-line coverage summary for managers — replaces the multi-team
 * capacity list. Managers see one team, so the existing list was
 * mostly chrome. Today's slot usage + worst day this week is the whole
 * signal Hafiz needs at a glance.
 *
 * Click expands to a two-week per-day strip (this week + next week).
 * Next week's data is lazy-loaded via a second dashboard query gated
 * on the expanded state — collapsed renders never pay the cost.
 */
export function CoverageRail({
  team,
  weekDays,
  weekStart,
  horizonDays,
}: CoverageRailProps): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  const nextWeekStart = useMemo(
    () => (weekStart ? addDaysIso(weekStart, 7) : null),
    [weekStart],
  )

  // Lazy-fetch next week's coverage only when expanded — Hafiz's "rare
  // click" criterion. The query is keyed on the next Monday so two
  // managers in different weeks don't share a cache entry.
  const nextWeekQuery = useQuery({
    queryKey: ['dashboard', 'coverage-rail-week2', nextWeekStart],
    queryFn: () => getDashboard({ weekStart: nextWeekStart ?? undefined, horizonDays: 30 }),
    enabled: expanded && nextWeekStart !== null,
    staleTime: 60_000,
  })

  if (!team) {
    return (
      <Card padding="10px 16px" data-testid="coverage-rail">
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Coverage unavailable.</span>
      </Card>
    )
  }

  const slack = team.slotsToday - team.onLeaveToday
  const todayFull = team.onLeaveToday >= team.slotsToday
  const todayKind: 'high' | 'medium' | 'info' = todayFull ? 'high' : slack <= 1 ? 'medium' : 'info'

  // Pick the worst upcoming day (lowest slack, working days only) from
  // the week-triage payload when present. Same rule as the queue row's
  // tight-day pill so the two signals stay consistent.
  const worst = (weekDays ?? []).reduce<{ date: string; slack: number } | null>((acc, d) => {
    if (d.isWeekend || d.isHoliday) return acc
    const dSlack = d.slotsTotal - d.onLeaveApproved - d.onLeavePending
    if (acc === null || dSlack < acc.slack) return { date: d.date, slack: dSlack }
    return acc
  }, null)

  const worstKind: 'high' | 'medium' | 'info' | null =
    worst === null ? null : worst.slack <= 0 ? 'high' : worst.slack === 1 ? 'medium' : null

  const todayIso = new Date().toISOString().slice(0, 10)
  const nextWeekDays = nextWeekQuery.data?.weekTriage?.days ?? []

  return (
    <Card padding="10px 16px" data-testid="coverage-rail">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          background: 'transparent',
          border: 0,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          color: 'inherit',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Coverage</span>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          Today{' '}
          <strong style={{ color: 'var(--text)' }}>
            {team.onLeaveToday}/{team.slotsToday}
          </strong>
        </span>
        <Severity kind={todayKind}>
          {todayFull ? 'Full' : `${slack} open`}
        </Severity>
        {worstKind && worst ? (
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            ·{' '}
            <Severity kind={worstKind}>
              {`${shortWeekday(worst.date)} tight`}
            </Severity>
          </span>
        ) : null}
        <span
          aria-hidden
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: 'var(--text-3)',
          }}
        >
          {expanded ? 'Hide' : 'Details'}
        </span>
      </button>

      {expanded ? (
        <div
          data-testid="coverage-rail-details"
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid var(--french-gray-2)',
            fontSize: 12,
            color: 'var(--text-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div>
            <strong style={{ color: 'var(--text)' }}>{team.name}</strong> · {team.memberCount}{' '}
            members · {team.pendingCount} pending
          </div>

          {weekDays && weekDays.length > 0 ? (
            <WeekRow label="This wk" days={weekDays} todayIso={todayIso} />
          ) : null}

          {nextWeekStart ? (
            nextWeekQuery.isLoading ? (
              <div
                data-testid="coverage-rail-week2-loading"
                style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: 64 }}
              >
                Loading next week…
              </div>
            ) : nextWeekDays.length > 0 ? (
              <WeekRow label="Next wk" days={nextWeekDays} todayIso={todayIso} />
            ) : null
          ) : null}

          <div style={{ marginTop: 4, color: 'var(--text-3)' }}>
            Next {horizonDays} days: {team.tightDays} tight day
            {team.tightDays === 1 ? '' : 's'}, {team.fullDays.count} full day
            {team.fullDays.count === 1 ? '' : 's'}.
          </div>
        </div>
      ) : null}
    </Card>
  )
}
